/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {FC} from 'react';
import {
  IconButton,
  makeStyles,
  Theme,
  Typography
} from '@material-ui/core';
import classnames from 'classnames';
import {dlsColor} from '../../theme/theme';

// defines the styles used by the component
const useStyles = makeStyles((theme: Theme) => ({
  leftBorderRadius : {
    borderRadius : '50% 0 0 50%'
  },
  rightBorderRadius : {
    borderRadius : '0 50% 50% 0'
  },
  buttonContainer : {
    display : 'flex'
  },
  button : {
    height  : 36,
    width   : 36,
    padding : 0
  },
  buttonText : {
    lineHeight    : '24px',
    letterSpacing : '1px',
    fontSize      : '16px',
    fontWeight    : ({fromDateTime}: {fromDateTime: boolean}) => fromDateTime ? 400 : 'bold'
  },
  outlined : {
    border : `1px solid ${theme.palette.primary.dark}`
  },
  filled : {
    '&:hover' : {
      backgroundColor : 'rgba(2, 171, 202, 0.5)'
    },
    backgroundColor : ({fromDateTime}: {fromDateTime: boolean}) => {
      return fromDateTime ? dlsColor('darkTeal') : 'rgba(2, 171, 202, 0.5)';
    }
  },
  highlighted : {
    backgroundColor : 'rgba(157, 232, 245, 0.3)'
  },
  contrast : {
    color : theme.palette.primary.contrastText
  },
  noVisibility : {
    visibility : 'hidden'
  }
}));

// defines the prop definitions for the component
export interface DayProps {
  filled?: boolean;
  outlined?: boolean;
  highlighted?: boolean;
  disabled?: boolean;
  startOfRange?: boolean;
  endOfRange?: boolean;
  onClick?: () => void;
  onHover?: () => void;
  value: number | string;
  fromDateTime?: boolean;
}

/**
 * @Day
 *
 * Defines the Day component.
 */
export const Day: FC<DayProps> = ({
  startOfRange,
  endOfRange,
  disabled,
  highlighted,
  outlined,
  filled,
  onClick,
  onHover,
  value,
  fromDateTime
}: DayProps) => {
  const classes = useStyles({fromDateTime});

  return (
    <div className={classnames(
      (disabled && fromDateTime) && classes.noVisibility,
      classes.buttonContainer,
      startOfRange && classes.leftBorderRadius,
      endOfRange && classes.rightBorderRadius,
      !disabled && highlighted && classes.highlighted
    )}>
      <IconButton disabled={disabled}
        onClick={onClick}
        onMouseOver={onHover}
        className={classnames(
          classes.button,
          !disabled && outlined && classes.outlined,
          !disabled && filled && classes.filled
        )}>
        <Typography color={!disabled ? 'textPrimary' : 'textSecondary'}
          className={classnames(
            classes.buttonText,
            !disabled && filled && classes.contrast
          )}
          variant='body2'>
          {value}
        </Typography>
      </IconButton>
    </div>
  );
};

export default Day;
